import { isArray } from 'lodash-es';
import React, { useCallback, useEffect, useState } from 'react';
import { usePopper } from 'react-popper';
import { useResizeDetector } from 'react-resize-detector';
import { PopoverProvider } from './PopoverContext';
import sameWidthModifier from './sameWidthModifier';
import { PopoverContainerProps } from './types';
import { preventOverflowModifier } from './utils';

/**
 * Handles positioning and display of the Popover.  The child component should use
 * usePopover to read props passed down from this component.
 */
const PopoverContainer: React.FC<PopoverContainerProps> = ({
  arrow = false,
  flip = true,
  isOpen,
  keepMounted,
  offset = 0,
  placement = 'top',
  preventOverflow,
  triggerElement,
  children,
  updatePositionOnTriggerResize,
  width = 'auto',
}) => {
  const [popoverElement, setPopoverElement] = useState<HTMLElement | null>(
    null,
  );

  const [arrowElement, setArrowElement] = useState<HTMLElement | null>(null);

  const resizeTargetRef = React.useRef<Element | null>();

  useEffect(() => {
    // As the trigger element is not a ref and it might be changed by the parent element,
    // the ref is updated if it changes.
    // For cases where the trigger is not an element, the ref is reset.
    if (triggerElement instanceof Element) {
      resizeTargetRef.current = triggerElement;
    } else {
      resizeTargetRef.current = null;
    }
  }, [triggerElement]);

  const { styles, attributes, update } = usePopper(
    triggerElement,
    popoverElement,
    {
      modifiers: [
        {
          name: 'offset',
          options: { offset: isArray(offset) ? offset : [0, offset] },
        },
        { name: 'arrow', options: { element: arrowElement } },
        { name: 'eventListeners', options: { scroll: false } },
        { name: 'flip', enabled: flip },
        preventOverflowModifier(preventOverflow),
        sameWidthModifier({ enabled: width === 'target' }),
      ],
      placement,
    },
  );

  const handleResize = useCallback(() => {
    if (updatePositionOnTriggerResize) {
      update?.();
    }
  }, [update, updatePositionOnTriggerResize]);

  useResizeDetector({ onResize: handleResize, targetRef: resizeTargetRef });

  if (!isOpen && !keepMounted) {
    return null;
  }

  return (
    <PopoverProvider
      arrow={arrow}
      arrowStyle={styles.arrow}
      isOpen={!!isOpen}
      popoverAttributes={attributes.popper}
      popoverRef={setPopoverElement}
      popoverStyle={styles.popper}
      arrowRef={setArrowElement}
    >
      {children}
    </PopoverProvider>
  );
};

export default PopoverContainer;
